/**
 * Google Sheets Service
 * Handles all Google Sheets integration related operations
 */

import { apiRequest } from '../utils/apiResponseHandler';
import { getApiUrl } from '../config/api';

const googleSheetsService = {
  // ==================== GOOGLE SHEETS INTEGRATION ====================

  // Sync Member to Sheets
  async syncMemberToSheets(memberId) {
    return await apiRequest(getApiUrl('MEMBER_SYNC_TO_SHEETS', memberId), {
      method: 'POST',
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Bulk Sync to Sheets
  async bulkSyncToSheets(syncData) {
    return await apiRequest(getApiUrl('BULK_SYNC_TO_SHEETS'), {
      method: 'POST',
      body: JSON.stringify(syncData),
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Get Backup Dashboard
  async getBackupDashboard() {
    return await apiRequest(getApiUrl('BACKUP_DASHBOARD'), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Trigger Manual Backup Sync
  async triggerBackupSync() {
    return await apiRequest(getApiUrl('BACKUP_TRIGGER'), {
      method: 'POST',
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Get Sync Log Details
  async getSyncLogDetails(logId) {
    return await apiRequest(getApiUrl('BACKUP_SYNC_LOG_DETAILS', logId), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Test Backup Connection
  async testBackupConnection() {
    return await apiRequest(getApiUrl('BACKUP_TEST_CONNECTION'), {
      method: 'GET',
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Get Connection Status
  async getConnectionStatus() {
    return await apiRequest(getApiUrl('BACKUP_TEST_CONNECTION'), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: false
    });
  },

  // Import from Google Sheets
  async importFromSheets(params = {}) {
    const {
      sheet_type = 'all',
      dry_run = false,
      force = false,
      financial_period = null
    } = params;

    const payload = {
      sheet_type,
      dry_run,
      force
    };

    if (financial_period) {
      payload.financial_period = financial_period;
    }

    return await apiRequest(getApiUrl('BACKUP_IMPORT_FROM_SHEETS'), {
      method: 'POST',
      body: JSON.stringify(payload),
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Preview Import (Dry Run)
  async previewImport(sheetType = 'all', financialPeriod = null) {
    return await this.importFromSheets({
      sheet_type: sheetType,
      dry_run: true,
      force: false,
      financial_period: financialPeriod
    });
  },

  // Force Import (Ignore Errors)
  async forceImport(sheetType = 'all', financialPeriod = null) {
    return await this.importFromSheets({
      sheet_type: sheetType,
      dry_run: false,
      force: true,
      financial_period: financialPeriod
    });
  },

  // Get Backup Statistics
  async getBackupStatistics() {
    return await apiRequest(getApiUrl('BACKUP_STATISTICS'), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Update Backup Settings
  async updateBackupSettings(settings) {
    return await apiRequest(getApiUrl('BACKUP_SETTINGS_UPDATE'), {
      method: 'POST',
      body: settings,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // ==================== BACKUP SYNC LOGS MANAGEMENT ====================

  // List Backup Sync Logs
  async getBackupSyncLogs(filters = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });
    
    const url = queryParams.toString() 
      ? `${getApiUrl('BACKUP_SYNC_LOGS')}?${queryParams.toString()}`
      : getApiUrl('BACKUP_SYNC_LOGS');
    
    return await apiRequest(url, {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Get Backup Sync Log by ID
  async getBackupSyncLogById(logId) {
    return await apiRequest(getApiUrl('BACKUP_SYNC_LOG_BY_ID', logId), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // ==================== MISSING ENDPOINT IMPLEMENTATIONS ====================

  // Get Sync Logs with Filters (ViewSet endpoint)
  async getSyncLogs(filters = {}) {
    const queryParams = new URLSearchParams();
    
    if (filters.sync_type) queryParams.append('sync_type', filters.sync_type);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.date_from) queryParams.append('date_from', filters.date_from);
    if (filters.date_to) queryParams.append('date_to', filters.date_to);
    if (filters.ordering) queryParams.append('ordering', filters.ordering);

    const url = getApiUrl('BACKUP_SYNC_LOGS') + (queryParams.toString() ? `?${queryParams.toString()}` : '');
    
    return await apiRequest(url, {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Get Sync Log by ID (ViewSet endpoint)
  async getSyncLogById(logId) {
    return await apiRequest(getApiUrl('BACKUP_SYNC_LOG_BY_ID', logId), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },


  // Trigger Backup Sync with Parameters
  async triggerBackupSyncWithParams(syncData = {}) {
    return await apiRequest(getApiUrl('BACKUP_TRIGGER'), {
      method: 'POST',
      body: JSON.stringify(syncData),
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Get Backup Statistics with Parameters
  async getBackupStatisticsWithParams(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.days) queryParams.append('days', params.days);

    const url = getApiUrl('BACKUP_STATISTICS') + (queryParams.toString() ? `?${queryParams.toString()}` : '');
    
    return await apiRequest(url, {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // ==================== UTILITY FUNCTIONS ====================



  // Get Sync Status Badge
  getSyncStatusBadge(log) {
    const status = log.status || log.sync_status;
    switch (status) {
      case 'completed':
      case 'success':
        return { text: 'Completed', class: 'completed', color: '#38a169' };
      case 'failed':
      case 'error':
        return { text: 'Failed', class: 'failed', color: '#e53e3e' };
      case 'in_progress':
      case 'running':
        return { text: 'In Progress', class: 'in-progress', color: '#3182ce' };
      case 'pending':
        return { text: 'Pending', class: 'pending', color: '#d69e2e' };
      case 'cancelled':
        return { text: 'Cancelled', class: 'cancelled', color: '#718096' };
      default:
        return { text: 'Unknown', class: 'unknown', color: '#718096' };
    }
  },

  // Format Sync Log for Display
  formatSyncLog(log) {
    const duration = log.duration_seconds ? 
      `${Math.round(log.duration_seconds)}s` : 
      (log.duration_display || 'N/A');
    
    const successRate = log.total_members > 0 ? 
      Math.round((log.synced_members / log.total_members) * 100) : 0;
    
    return {
      ...log,
      formatted_started_at: log.started_at ? 
        new Date(log.started_at).toLocaleString() : 'Unknown',
      formatted_completed_at: log.completed_at ? 
        new Date(log.completed_at).toLocaleString() : 'Not completed',
      duration_display: duration,
      success_rate_display: `${successRate}%`,
      sync_type_display: log.sync_type ? 
        log.sync_type.charAt(0).toUpperCase() + log.sync_type.slice(1) : 'Unknown',
      status_display: this.getSyncStatusBadge(log).text,
      status_badge: this.getSyncStatusBadge(log)
    };
  },

  // Get Sync Statistics
  getSyncStatistics(logs) {
    const total = logs.length;
    const completed = logs.filter(log => {
      const status = log.status || log.sync_status;
      return status === 'completed' || status === 'success';
    }).length;
    const failed = logs.filter(log => {
      const status = log.status || log.sync_status;
      return status === 'failed' || status === 'error';
    }).length;
    const inProgress = logs.filter(log => {
      const status = log.status || log.sync_status;
      return status === 'in_progress' || status === 'running';
    }).length;
    const pending = logs.filter(log => {
      const status = log.status || log.sync_status;
      return status === 'pending';
    }).length;
    const cancelled = logs.filter(log => {
      const status = log.status || log.sync_status;
      return status === 'cancelled';
    }).length;
    
    const successRate = total > 0 ? (completed / total) * 100 : 0;
    
    return {
      total,
      completed,
      failed,
      inProgress,
      pending,
      cancelled,
      successRate: Math.round(successRate * 100) / 100
    };
  }
};

export { googleSheetsService };
export default googleSheetsService;
